/**
 * Base UI Component System
 * All UI elements inherit from this base class
 */

import { getAudioManager } from '@client/audio/AudioManager';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface UIStyle {
  // Sizing
  width?: number | 'auto' | 'fill';
  height?: number | 'auto' | 'fill';
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;

  // Spacing
  padding?: number | Padding;
  margin?: number | Padding;
  gap?: number;

  // Layout
  flexDirection?: 'row' | 'column';
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  justifyContent?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';

  // Appearance
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;

  // Text
  textColor?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  textAlign?: 'left' | 'center' | 'right';
}

export interface MouseEvent {
  x: number;
  y: number;
  button: 'left' | 'right' | 'middle';
  type: 'down' | 'up' | 'move' | 'wheel';
  deltaY?: number;
  shiftKey?: boolean;
}

export abstract class UIComponent {
  // Position and size (computed after layout)
  protected bounds: Rect = { x: 0, y: 0, width: 0, height: 0 };

  // Style
  protected style: UIStyle = {};

  // State
  protected visible: boolean = true;
  protected enabled: boolean = true;
  protected hovered: boolean = false;
  protected focused: boolean = false;

  // Hierarchy
  protected parent: UIComponent | null = null;
  protected children: UIComponent[] = [];

  // Callbacks
  public onClick?: (e: MouseEvent) => void;
  public onHover?: (hovered: boolean) => void;

  constructor(style: Partial<UIStyle> = {}) {
    this.style = { ...this.getDefaultStyle(), ...style };
  }

  protected getDefaultStyle(): UIStyle {
    return {
      width: 'auto',
      height: 'auto',
      padding: 0,
      margin: 0,
      gap: 0,
      flexDirection: 'column',
      alignItems: 'start',
      justifyContent: 'start',
      borderWidth: 0,
      borderRadius: 0,
      fontSize: 14,
      fontFamily: 'Arial',
      fontWeight: 'normal',
      textAlign: 'left',
    };
  }

  // Hierarchy management
  addChild(child: UIComponent): void {
    child.parent = this;
    this.children.push(child);
  }

  removeChild(child: UIComponent): void {
    const index = this.children.indexOf(child);
    if (index >= 0) {
      child.parent = null;
      this.children.splice(index, 1);
    }
  }

  clearChildren(): void {
    for (const child of this.children) {
      child.parent = null;
    }
    this.children = [];
  }

  // Padding helper
  protected getPadding(): Padding {
    const p = this.style.padding;
    if (typeof p === 'number') {
      return { top: p, right: p, bottom: p, left: p };
    }
    return p || { top: 0, right: 0, bottom: 0, left: 0 };
  }

  protected getMargin(): Padding {
    const m = this.style.margin;
    if (typeof m === 'number') {
      return { top: m, right: m, bottom: m, left: m };
    }
    return m || { top: 0, right: 0, bottom: 0, left: 0 };
  }

  // Measure the natural size of this component
  abstract measureSize(ctx: CanvasRenderingContext2D): { width: number; height: number };

  // Layout: compute bounds for this component and children
  layout(ctx: CanvasRenderingContext2D, availableRect: Rect): void {
    const margin = this.getMargin();

    // Measure self
    const measured = this.measureSize(ctx);

    // Compute width
    let width: number;
    if (this.style.width === 'fill') {
      width = availableRect.width - margin.left - margin.right;
    } else if (this.style.width === 'auto' || this.style.width === undefined) {
      width = measured.width;
    } else {
      width = this.style.width;
    }

    // Apply constraints
    if (this.style.minWidth) width = Math.max(width, this.style.minWidth);
    if (this.style.maxWidth) width = Math.min(width, this.style.maxWidth);

    // Compute height
    let height: number;
    if (this.style.height === 'fill') {
      height = availableRect.height - margin.top - margin.bottom;
    } else if (this.style.height === 'auto' || this.style.height === undefined) {
      height = measured.height;
    } else {
      height = this.style.height;
    }

    // Apply constraints
    if (this.style.minHeight) height = Math.max(height, this.style.minHeight);
    if (this.style.maxHeight) height = Math.min(height, this.style.maxHeight);

    // Set bounds
    this.bounds = {
      x: availableRect.x + margin.left,
      y: availableRect.y + margin.top,
      width,
      height,
    };

    // Layout children
    this.layoutChildren(ctx);
  }

  protected layoutChildren(ctx: CanvasRenderingContext2D): void {
    if (this.children.length === 0) return;

    const padding = this.getPadding();
    const gap = this.style.gap || 0;
    const direction = this.style.flexDirection || 'column';

    // Content area
    const contentX = this.bounds.x + padding.left;
    const contentY = this.bounds.y + padding.top;
    const contentWidth = this.bounds.width - padding.left - padding.right;
    const contentHeight = this.bounds.height - padding.top - padding.bottom;

    // Measure all children first
    const childSizes = this.children.map(child => child.measureSize(ctx));

    // Calculate total size and layout
    if (direction === 'column') {
      let y = contentY;
      for (let i = 0; i < this.children.length; i++) {
        const child = this.children[i];
        const size = childSizes[i];

        // Apply alignment
        let x = contentX;
        let childWidth = size.width;

        if (this.style.alignItems === 'center') {
          x = contentX + (contentWidth - size.width) / 2;
        } else if (this.style.alignItems === 'end') {
          x = contentX + contentWidth - size.width;
        } else if (this.style.alignItems === 'stretch') {
          childWidth = contentWidth;
        }

        child.layout(ctx, { x, y, width: childWidth, height: size.height });
        y += child.bounds.height + (i < this.children.length - 1 ? gap : 0);
      }
    } else {
      // Row layout
      let x = contentX;
      for (let i = 0; i < this.children.length; i++) {
        const child = this.children[i];
        const size = childSizes[i];

        // Apply alignment
        let y = contentY;
        let childHeight = size.height;

        if (this.style.alignItems === 'center') {
          y = contentY + (contentHeight - size.height) / 2;
        } else if (this.style.alignItems === 'end') {
          y = contentY + contentHeight - size.height;
        } else if (this.style.alignItems === 'stretch') {
          childHeight = contentHeight;
        }

        child.layout(ctx, { x, y, width: size.width, height: childHeight });
        x += child.bounds.width + (i < this.children.length - 1 ? gap : 0);
      }
    }
  }

  // Render this component and children
  render(ctx: CanvasRenderingContext2D): void {
    if (!this.visible) return;

    // Render background and border
    this.renderBackground(ctx);

    // Render self content
    this.renderContent(ctx);

    // Render children
    for (const child of this.children) {
      child.render(ctx);
    }
  }

  protected renderBackground(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const radius = this.style.borderRadius || 0;

    if (this.style.backgroundColor) {
      ctx.fillStyle = this.style.backgroundColor;
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.fill();
    }

    if (this.style.borderColor && this.style.borderWidth) {
      ctx.strokeStyle = this.style.borderColor;
      ctx.lineWidth = this.style.borderWidth;
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.stroke();
    }
  }

  protected drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // Override in subclasses to render content
  protected abstract renderContent(ctx: CanvasRenderingContext2D): void;

  // Mouse handling
  handleMouseEvent(e: MouseEvent): boolean {
    if (!this.visible || !this.enabled) return false;

    const inside = this.containsPoint(e.x, e.y);

    // Update hover state
    if (e.type === 'move') {
      const wasHovered = this.hovered;
      this.hovered = inside;
      if (wasHovered !== this.hovered && this.onHover) {
        this.onHover(this.hovered);
      }
    }

    // Check children first (reverse order for z-index)
    for (let i = this.children.length - 1; i >= 0; i--) {
      if (this.children[i].handleMouseEvent(e)) {
        return true;
      }
    }

    // Handle click
    if (inside && e.type === 'down' && e.button === 'left') {
      if (this.onClick) {
        getAudioManager().play('ui_confirm');
        this.onClick(e);
        return true;
      }
    }

    return false;
  }

  containsPoint(x: number, y: number): boolean {
    return (
      x >= this.bounds.x &&
      x <= this.bounds.x + this.bounds.width &&
      y >= this.bounds.y &&
      y <= this.bounds.y + this.bounds.height
    );
  }

  // Getters
  getBounds(): Rect {
    return { ...this.bounds };
  }

  isVisible(): boolean {
    return this.visible;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setStyle(style: Partial<UIStyle>): void {
    this.style = { ...this.style, ...style };
  }
}
